import styled from "styled-components";
import { Box, Flex, Text } from "rebass";
import {
  HiCheck,
  HiExternalLink,
  HiExclamationCircle,
  HiChevronDown,
  HiCheckCircle,
  HiSparkles,
} from "react-icons/hi";
import { HiOutlineXCircle } from "react-icons/hi2";
import { SecondaryButton } from "../styledComponents";
import {
  IClipForDeletion,
  IStorageStatus,
  TSavedFilter,
  TSelectionMode,
} from "../@types";
import { formatNumber } from "MVVM/Utilities/Clip";

type SortOption = "LATEST" | "VIEWS" | "BEST";

interface DeleteBrowseProps {
  clips: IClipForDeletion[];
  isLoading: boolean;
  selectedClipIds: string[];
  onSelectClip: (clipId: string) => void;
  isClipSelectable: (clip: IClipForDeletion) => boolean;
  onSortChange: (sort: SortOption) => void;
  currentSort: SortOption;
  onDelete: () => void;
  onBack: () => void;
  isDeleting: boolean;
  backButtonText?: string;
  hideSortOptions?: boolean;
  onOpenClip?: (clipId: string) => void;
  selectionMode: TSelectionMode;
  storageStatus?: IStorageStatus;
  onUpgrade?: () => void;
  isUnlimited?: boolean;
  savedFilter: TSavedFilter;
  onSavedFilterChange: (filter: TSavedFilter) => void;
  manageMode?: boolean;
  hasNextPage?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  onLetAllstarChoose?: () => void;
}

export const DeleteBrowse = ({
  clips,
  isLoading,
  selectedClipIds,
  onSelectClip,
  isClipSelectable,
  onSortChange,
  currentSort,
  onDelete,
  onBack,
  isDeleting,
  backButtonText = "Close",
  hideSortOptions = false,
  onOpenClip,
  selectionMode,
  storageStatus,
  onUpgrade,
  isUnlimited = false,
  savedFilter,
  onSavedFilterChange,
  manageMode = false,
  hasNextPage = false,
  loadingMore = false,
  onLoadMore,
  onLetAllstarChoose,
}: DeleteBrowseProps) => {
  const handleOpenClip = (e: React.MouseEvent, clipId: string) => {
    e.stopPropagation();
    onOpenClip?.(clipId);
  };

  const showStorageBar =
    storageStatus && !isUnlimited && storageStatus.limit > 0;
  const percentage = showStorageBar
    ? (storageStatus.savedCount / storageStatus.limit) * 100
    : 0;

  const getActionLabel = () => {
    if (selectionMode === "save") return "Save";
    return "Unsave";
  };

  const getProcessingLabel = () => {
    if (selectionMode === "save") return "Saving...";
    return "Unsaving...";
  };

  return (
    <>
      {showStorageBar && (
        <StorageSection p="20px 28px">
          <StorageRow mb="10px">
            <StorageCount>
              <EnvyCount>{storageStatus.savedCount}</EnvyCount> /{" "}
              {storageStatus.limit} clips stored
            </StorageCount>
            {onUpgrade && (
              <UpgradeLink onClick={onUpgrade}>Upgrade →</UpgradeLink>
            )}
          </StorageRow>
          <ProgressBar>
            <ProgressFill $percentage={percentage} />
          </ProgressBar>
        </StorageSection>
      )}

      <Box p="16px 28px 24px">
        {manageMode &&
          onLetAllstarChoose &&
          showStorageBar &&
          storageStatus.savedCount < storageStatus.limit && (
            <AllstarChooseRow mb="12px">
              <AllstarChooseButton onClick={onLetAllstarChoose}>
                <HiSparkles size={16} />
                Let Allstar Choose for Me
              </AllstarChooseButton>
              <AllstarChooseHint>
                Automatically picks your best unsaved clips based on views and
                gameplay score
              </AllstarChooseHint>
            </AllstarChooseRow>
          )}
        <ControlsRow mb="12px">
          {manageMode && (
            <FilterRow>
              <FilterPill
                $active={savedFilter === "all"}
                onClick={() => onSavedFilterChange("all")}
              >
                All
              </FilterPill>
              <FilterPill
                $active={savedFilter === "saved"}
                onClick={() => onSavedFilterChange("saved")}
              >
                Saved
              </FilterPill>
              <FilterPill
                $active={savedFilter === "unsaved"}
                onClick={() => onSavedFilterChange("unsaved")}
              >
                Expiring
              </FilterPill>
            </FilterRow>
          )}
          {!hideSortOptions && (
            <SortRow>
              <SortLabel>Sort by</SortLabel>
              <SelectWrapper>
                <SortSelect
                  value={currentSort}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    onSortChange(e.target.value as SortOption)
                  }
                >
                  <option value="LATEST">Latest</option>
                  <option value="VIEWS">Views</option>
                  <option value="BEST">Score</option>
                </SortSelect>
                <SelectIcon>
                  <HiChevronDown size={14} />
                </SelectIcon>
              </SelectWrapper>
            </SortRow>
          )}
        </ControlsRow>

        <ClipList>
          {isLoading ? (
            <>
              {[...Array(4)].map((_, i) => (
                <SkeletonRow key={i} style={{ marginBottom: i < 3 ? 2 : 0 }}>
                  <SkeletonCheckbox />
                  <SkeletonThumbnail />
                  <SkeletonInfo>
                    <SkeletonTitle />
                    <SkeletonMeta />
                  </SkeletonInfo>
                </SkeletonRow>
              ))}
            </>
          ) : clips.length === 0 ? (
            <EmptyText>No clips found</EmptyText>
          ) : (
            <>
              {clips.map((clip) => {
                const selectable = isClipSelectable(clip);
                const isSelected = selectedClipIds.includes(clip.id);

                return (
                  <ClipRow
                    key={clip.id}
                    onClick={() => selectable && onSelectClip(clip.id)}
                    $selected={isSelected}
                    $disabled={!selectable}
                  >
                    <Checkbox
                      $checked={isSelected}
                      $mode={selectionMode}
                      $disabled={!selectable}
                    >
                      {isSelected && <HiCheck size={12} />}
                    </Checkbox>
                    <ClipThumbnail src={clip.thumbnailUrl} alt={clip.title} />
                    <ClipInfo>
                      <ClipTitle>{clip.title}</ClipTitle>
                      <ClipMeta>{formatNumber(clip.views)} views</ClipMeta>
                    </ClipInfo>
                    <SavedIndicator $isSaved={clip.isSaved}>
                      {clip.isSaved ? (
                        <HiCheckCircle size={16} />
                      ) : (
                        <HiOutlineXCircle size={16} />
                      )}
                    </SavedIndicator>
                    {onOpenClip && (
                      <OpenButton onClick={(e) => handleOpenClip(e, clip.id)}>
                        <HiExternalLink size={16} />
                      </OpenButton>
                    )}
                  </ClipRow>
                );
              })}
              {hasNextPage && onLoadMore && (
                <LoadMoreButton onClick={onLoadMore} disabled={loadingMore}>
                  {loadingMore ? "Loading..." : "Load More"}
                </LoadMoreButton>
              )}
            </>
          )}
        </ClipList>

        <PolicyNote mt="12px" $visible={selectedClipIds.length > 0}>
          <HiExclamationCircle size={14} style={{ flexShrink: 0 }} />
          {isUnlimited
            ? "Your account is exempt from clip expiration policies."
            : selectionMode === "save"
            ? "Selected clips will be saved permanently to your storage."
            : "Unsaved clips will convert to drafts and are subject to expiration."}
        </PolicyNote>
      </Box>

      <ModalFooter>
        <SecondaryButton onClick={onBack} disabled={isDeleting}>
          {backButtonText}
        </SecondaryButton>
        <ActionButton
          onClick={onDelete}
          disabled={selectedClipIds.length === 0 || isDeleting}
          $mode={selectionMode}
        >
          {isDeleting
            ? getProcessingLabel()
            : `${getActionLabel()} Selected (${selectedClipIds.length})`}
        </ActionButton>
      </ModalFooter>
    </>
  );
};

const ModalFooter = styled(Flex)`
  border-top: 1px solid ${({ theme }) => theme.colors.navy50};
  gap: 12px;
  justify-content: flex-end;
  padding: 16px 28px;
`;

const AllstarChooseRow = styled(Box)`
  border: 1px solid ${({ theme }) => theme.colors.navy50};
  border-radius: 10px;
  padding: 12px 16px;
  text-align: center;
`;

const AllstarChooseButton = styled.button`
  align-items: center;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.envy};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.envy};
  cursor: pointer;
  display: inline-flex;
  font-family: Barlow;
  font-size: 14px;
  font-weight: 600;
  gap: 6px;
  padding: 8px 20px;
  transition: all 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.envy};
    color: ${({ theme }) => theme.colors.navy};
  }
`;

const AllstarChooseHint = styled(Text)`
  color: ${({ theme }) => theme.colors.shade400};
  font-family: Barlow;
  font-size: 12px;
  margin-top: 6px;
`;

const ControlsRow = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const FilterRow = styled(Flex)`
  align-items: center;
  background: ${({ theme }) => theme.colors.navy};
  border-radius: 8px;
  gap: 2px;
  padding: 3px;
`;

const FilterPill = styled.button<{ $active: boolean }>`
  background: ${({ theme, $active }) =>
    $active ? theme.colors.navy50 : "transparent"};
  border: none;
  border-radius: 6px;
  color: ${({ theme, $active }) =>
    $active ? theme.colors.ice : theme.colors.shade400};
  cursor: pointer;
  font-family: Barlow;
  font-size: 12px;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  padding: 5px 12px;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
  }
`;

const SortRow = styled(Flex)`
  align-items: center;
  gap: 8px;
  margin-left: auto;
`;

const SortLabel = styled(Text)`
  color: ${({ theme }) => theme.colors.shade400};
  font-family: Barlow;
  font-size: 13px;
`;

const SelectWrapper = styled.div`
  position: relative;
`;

const SortSelect = styled.select`
  appearance: none;
  background: ${({ theme }) => theme.colors.navy};
  border: 1px solid ${({ theme }) => theme.colors.navy50};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  font-family: Barlow;
  font-size: 13px;
  padding: 6px 28px 6px 10px;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.shade400};
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.envy};
    outline: none;
  }

  option {
    background: ${({ theme }) => theme.colors.navy};
    color: ${({ theme }) => theme.colors.ice};
  }
`;

const SelectIcon = styled.span`
  align-items: center;
  color: ${({ theme }) => theme.colors.shade400};
  display: flex;
  pointer-events: none;
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
`;

const ClipList = styled(Box)`
  height: 320px;
  overflow-y: auto;
`;

const ClipRow = styled(Flex)<{ $selected: boolean; $disabled?: boolean }>`
  align-items: center;
  background: ${({ $selected }) =>
    $selected ? "rgba(255, 255, 255, 0.03)" : "transparent"};
  border-radius: 8px;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  gap: 12px;
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1)};
  padding: 8px 10px;
  transition: all 0.15s ease;

  &:hover {
    background: ${({ $disabled }) =>
      $disabled ? "transparent" : "rgba(255, 255, 255, 0.05)"};
  }

  &:not(:last-child) {
    margin-bottom: 2px;
  }
`;

const Checkbox = styled(Box)<{
  $checked: boolean;
  $mode: TSelectionMode;
  $disabled?: boolean;
}>`
  align-items: center;
  background: ${({ theme, $checked, $mode }) => {
    if (!$checked) return theme.colors.navy50;
    return $mode === "save" ? theme.colors.envy : theme.colors.alert;
  }};
  border: none;
  border-radius: 5px;
  color: ${({ theme }) => theme.colors.ice};
  display: flex;
  flex-shrink: 0;
  height: 20px;
  justify-content: center;
  width: 20px;
`;

const SavedIndicator = styled(Flex)<{ $isSaved: boolean }>`
  align-items: center;
  color: ${({ theme, $isSaved }) =>
    $isSaved ? theme.colors.envy : theme.colors.shade400};
  flex-shrink: 0;
`;

const ClipThumbnail = styled.img`
  border-radius: 6px;
  flex-shrink: 0;
  height: 44px;
  object-fit: cover;
  width: 78px;
`;

const ClipInfo = styled(Box)`
  flex: 1;
  min-width: 0;
`;

const ClipTitle = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-family: Barlow;
  font-size: 15px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ClipMeta = styled(Text)`
  color: ${({ theme }) => theme.colors.shade400};
  font-family: Barlow;
  font-size: 13px;
`;

const shimmer = `
  @keyframes shimmer {
    0% {
      background-position: -200px 0;
    }
    100% {
      background-position: 200px 0;
    }
  }
`;

const SkeletonBase = styled(Box)`
  ${shimmer}
  animation: shimmer 1.5s ease-in-out infinite;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.navy50} 0%,
    ${({ theme }) => theme.colors.navy} 50%,
    ${({ theme }) => theme.colors.navy50} 100%
  );
  background-size: 200px 100%;
  border-radius: 6px;
`;

const SkeletonRow = styled(Flex)`
  align-items: center;
  gap: 12px;
  padding: 8px 10px;
`;

const SkeletonCheckbox = styled(SkeletonBase)`
  border-radius: 4px;
  flex-shrink: 0;
  height: 18px;
  width: 18px;
`;

const SkeletonThumbnail = styled(SkeletonBase)`
  flex-shrink: 0;
  height: 40px;
  width: 72px;
`;

const SkeletonInfo = styled(Box)`
  flex: 1;
  min-width: 0;
`;

const SkeletonTitle = styled(SkeletonBase)`
  height: 14px;
  margin-bottom: 6px;
  width: 70%;
`;

const SkeletonMeta = styled(SkeletonBase)`
  height: 12px;
  width: 40%;
`;

const EmptyText = styled(Text)`
  color: ${({ theme }) => theme.colors.shade400};
  font-family: Barlow;
  font-size: 14px;
  padding: 32px;
  text-align: center;
`;

const LoadMoreButton = styled.button`
  background: ${({ theme }) => theme.colors.navy};
  border: 1px solid ${({ theme }) => theme.colors.navy50};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  font-family: Barlow;
  font-size: 14px;
  font-weight: 500;
  margin: 8px auto 4px;
  display: block;
  padding: 8px 24px;
  transition: all 0.15s ease;
  width: fit-content;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.envy};
    color: ${({ theme }) => theme.colors.envy};
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

const OpenButton = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.shade400};
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  height: 32px;
  justify-content: center;
  transition: all 0.15s ease;
  width: 32px;

  &:hover {
    background: ${({ theme }) => theme.colors.navy50};
    color: ${({ theme }) => theme.colors.envy};
  }
`;

const ActionButton = styled.button<{ $mode?: TSelectionMode }>`
  background: ${({ theme, $mode }) =>
    $mode === "save" ? theme.colors.envy : theme.colors.alert};
  border: none;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  font-family: Barlow;
  font-size: 15px;
  font-weight: 600;
  padding: 12px 24px;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const PolicyNote = styled(Flex)<{ $visible: boolean }>`
  align-items: center;
  color: ${({ theme }) => theme.colors.shade300};
  font-family: Barlow;
  font-size: 13px;
  gap: 6px;
  line-height: 1.4;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.15s ease;
`;

const StorageSection = styled(Box)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.navy50};
`;

const StorageRow = styled(Flex)`
  align-items: center;
  justify-content: space-between;
`;

const StorageCount = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-family: Barlow;
  font-size: 18px;
  font-weight: 600;
`;

const EnvyCount = styled.span`
  color: ${({ theme }) => theme.colors.envy};
`;

const UpgradeLink = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.envy};
  cursor: pointer;
  font-family: Barlow;
  font-size: 14px;
  font-weight: 600;
  padding: 0;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const ProgressBar = styled(Box)`
  background: ${({ theme }) => theme.colors.navy};
  border-radius: 9px;
  height: 18px;
  overflow: hidden;
  width: 100%;
`;

const ProgressFill = styled(Box)<{ $percentage: number }>`
  background: ${({ theme, $percentage }) => {
    if ($percentage >= 90) return theme.colors.alert;
    if ($percentage >= 70) return theme.colors.caution;
    return theme.colors.envy;
  }};
  border-radius: 9px;
  height: 100%;
  transition: width 0.3s ease;
  width: ${({ $percentage }) => Math.min($percentage, 100)}%;
`;
